import { InvalidCell } from 'common/types/scalarSpreadsheet';
import {
  SCENARIO_WITHOUT_EQUITY_VALUE_ID,
  SHEET_ALIASES_CONSTANTS,
} from 'pages/ValuationsAllocation/common/constants/equityAllocation';
import { VALUATION_SUMMARY_KEY } from 'pages/ValuationsAllocation/common/constants/valuationSummary';
import { InvalidCellEntry, InvalidTable } from 'pages/ValuationsAllocation/types';
import { describeInvalidCell, withDistinctLabels } from 'pages/ValuationsAllocation/util/getInvalidTables';
import { getNumberValue } from 'utilities';
import { GetScenariosWithoutEquityValueParams } from './types';

type Scenario = NonNullable<GetScenariosWithoutEquityValueParams['allocationScenarios']>[number];
type Sheet = NonNullable<GetScenariosWithoutEquityValueParams['equityAllocationSheet']>;
type SheetCell = NonNullable<Sheet['cells']>[string] & { data?: { scenarioId?: number; scenarioRef?: string } };

const { EQUITY_ALLOCATION_SPREADSHEET_PRESENT_EQUITY_VALUE } = SHEET_ALIASES_CONSTANTS;

// The stored pair the backend also checks: allocated values next to an equity value of 0 or less.
const hasNoEquityValue = (scenario: Scenario) =>
  getNumberValue(scenario.equity_value) <= 0 && getNumberValue(scenario.total_aggregate_value) > 0;

// A cell belongs to a scenario by its saved id, or by the draft reference of an unsaved scenario.
const belongsTo = (cell: SheetCell, { id, scenario_ref: scenarioRef }: Scenario) => {
  const { scenarioId, scenarioRef: cellScenarioRef } = cell.data ?? {};
  return Boolean((id && scenarioId === id) || (scenarioRef && cellScenarioRef === scenarioRef));
};

// The scenario's Present Equity Value cell as the banner describes it, with its grid position and sheet.
const describePresentEquityValueCell = (
  sheet: Sheet,
  scenario: Scenario,
  allocationMethods: GetScenariosWithoutEquityValueParams['allocationMethods']
): InvalidCellEntry | undefined => {
  const cell = Object.values(sheet.cells ?? {}).find(
    (candidate: SheetCell) =>
      candidate?.alias === EQUITY_ALLOCATION_SPREADSHEET_PRESENT_EQUITY_VALUE && belongsTo(candidate, scenario)
  );
  const [, columnLegend = '', rowNumber = ''] = /^([A-Z]+)(\d+)$/.exec(cell?.key ?? '') ?? [];
  if (!cell || !columnLegend) {
    return undefined;
  }
  return {
    ...describeInvalidCell({ ...cell, columnLegend, rowNumber: Number(rowNumber), sheet } as InvalidCell, {
      allocationMethods,
      tableNames: {},
    }),
    message: `The equity value that would be saved is ${getNumberValue(scenario.equity_value)}; it must be above 0.`,
  };
};

export const isScenarioWithoutEquityValueEntry = (table: InvalidTable) =>
  table.check === SCENARIO_WITHOUT_EQUITY_VALUE_ID;

// One banner entry per scenario the save must refuse, named by its column header and pointing at its
// Present Equity Value cell on the summary tab. Without the sheet the entry names the scenario by position.
const getScenariosWithoutEquityValue = ({
  allocationMethods,
  allocationScenarios = [],
  equityAllocationSheet,
}: GetScenariosWithoutEquityValueParams): InvalidTable[] => {
  const offending = allocationScenarios
    .map((scenario, index) => ({ index, scenario }))
    .filter(({ scenario }) => Boolean(scenario) && hasNoEquityValue(scenario))
    .map(({ index, scenario }) => ({
      cell: equityAllocationSheet && describePresentEquityValueCell(equityAllocationSheet, scenario, allocationMethods),
      index,
    }));
  // Two scenarios with the same header get the column letter added, as the regular cell lines do.
  const labelledCells = withDistinctLabels(offending.flatMap(({ cell }) => (cell ? [cell] : [])));

  return offending.map(({ cell, index }) => {
    const labelledCell = cell && labelledCells.shift();
    const label = labelledCell?.columnLabel || `Scenario ${index + 1}`;
    return {
      approachType: '',
      check: SCENARIO_WITHOUT_EQUITY_VALUE_ID,
      id: VALUATION_SUMMARY_KEY,
      name: `Equity Allocation scenario "${label}" has no equity value`,
      ...(labelledCell && { cells: [labelledCell] }),
    };
  });
};

export default getScenariosWithoutEquityValue;
